import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BuildingIcon, MapPinIcon } from 'lucide-react';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  Pagination,
  SearchInput,
  SkeletonTable,
  type DataTableHeader,
} from '@bcecloud/portal-ui';
import { errorMessage, getJson } from '@/lib/api';
import { EventsTable } from '@/components/EventsTable';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';

/**
 * Place directories and their event views, all computed under the caller's
 * role filter: a contractor's city list only counts their own events. Lists
 * are fully loaded, so search and column sort happen client-side.
 *
 *   /cities            — cities you have events in
 *   /venues            — venues you have events at
 *   /places/events?city=&usState=&venue=  — the events for one place
 */

/** Directory pages show 25 rows at a time. */
const FACET_PAGE_SIZE = 25;

interface CityFacet {
  readonly city?: string;
  readonly state?: string;
  readonly venue?: string;
  readonly venueAccountId?: string | null;
  readonly count: number;
  readonly upcomingCount: number;
  readonly lastEventAt: string | null;
}

function facetDate(value: string | null): string {
  return value
    ? new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })
    : '—';
}

function placeLink(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value);
  }
  return `/places/events?${search.toString()}`;
}

function FacetDirectory({
  by,
  eyebrow,
  title,
  subtitle,
  headers,
  searchFields,
  searchPlaceholder,
  row,
}: {
  readonly by: 'city' | 'venue';
  readonly eyebrow: string;
  readonly title: string;
  readonly subtitle: string;
  readonly headers: readonly DataTableHeader[];
  readonly searchFields: ReadonlyArray<keyof CityFacet>;
  readonly searchPlaceholder: string;
  readonly row: (facet: CityFacet) => React.ReactElement;
}): React.ReactElement {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const sortKeys = headers.flatMap((header) =>
    typeof header === 'string' || header.sortKey === undefined ? [] : [header.sortKey],
  );
  const { sort, onSort } = useTableSort(sortKeys);
  const facets = useQuery({
    queryKey: ['event-facets', by],
    queryFn: ({ signal }) => getJson<CityFacet[]>('/events/facets', { by }, signal),
  });
  const rows =
    facets.data === undefined ? [] : sortRows(filterRows(facets.data, search, searchFields), sort);
  // A new search or sort starts back at page 1 — page 4 of a different list
  // is meaningless.
  useEffect(() => {
    setPage(1);
  }, [search, sort?.key, sort?.direction]);
  const totalPages = Math.max(1, Math.ceil(rows.length / FACET_PAGE_SIZE));
  const visible = rows.slice((page - 1) * FACET_PAGE_SIZE, page * FACET_PAGE_SIZE);

  return (
    <section className="animate-portal-rise">
      <PageHeading eyebrow={eyebrow} title={title} subtitle={subtitle} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label={`Search ${title.toLowerCase()}`}
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
      </div>
      {facets.isPending ? (
        <SkeletonTable label="Loading…" />
      ) : facets.isError ? (
        <ErrorNotice title="Unavailable" message={errorMessage(facets.error)} />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<MapPinIcon aria-hidden className="size-6" />}
            title={search === '' ? 'Nothing here yet' : 'No matches'}
            message={
              search === ''
                ? "These fill in from the events you're associated with."
                : `Nothing matches "${search}".`
            }
          />
        </Panel>
      ) : (
        <>
          <DataTable headers={headers} minWidth={720} {...(sort ? { sort } : {})} onSort={onSort}>
            {visible.map(row)}
          </DataTable>
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            summary={`${rows.length} row${rows.length === 1 ? '' : 's'}`}
          />
        </>
      )}
    </section>
  );
}

export function CitiesPage(): React.ReactElement {
  return (
    <FacetDirectory
      by="city"
      eyebrow="PLACES"
      title="Cities"
      subtitle="Everywhere your events happen — click through for a city's (or state's) events."
      headers={[
        { label: 'City', sortKey: 'city' },
        { label: 'State', sortKey: 'state' },
        { label: 'Events', sortKey: 'count' },
        { label: 'Upcoming', sortKey: 'upcomingCount' },
        { label: 'Last event', sortKey: 'lastEventAt' },
      ]}
      searchFields={['city', 'state']}
      searchPlaceholder="City or state"
      row={(facet) => (
        <tr key={`${facet.city}-${facet.state}`} className="border-t border-portal-line">
          <td className="p-3">
            <Link
              to={placeLink({ city: facet.city, usState: facet.state })}
              className="text-portal-small font-bold text-brand hover:underline"
            >
              {facet.city}
            </Link>
          </td>
          <td className="p-3">
            {facet.state ? (
              <Link
                to={placeLink({ usState: facet.state })}
                className="text-portal-small text-brand hover:underline"
              >
                {facet.state}
              </Link>
            ) : (
              <span className="text-portal-small text-portal-muted">—</span>
            )}
          </td>
          <td className="p-3">
            <Pill>{facet.count}</Pill>
          </td>
          <td className="p-3">
            {facet.upcomingCount > 0 ? (
              <Pill tone="success">{facet.upcomingCount}</Pill>
            ) : (
              <span className="text-portal-small text-portal-muted">—</span>
            )}
          </td>
          <td className="p-3 whitespace-nowrap text-portal-small text-portal-muted">
            {facetDate(facet.lastEventAt)}
          </td>
        </tr>
      )}
    />
  );
}

export function VenuesPage(): React.ReactElement {
  return (
    <FacetDirectory
      by="venue"
      eyebrow="PLACES"
      title="Venues"
      subtitle="Everywhere your events have run — click through for a venue's events."
      headers={[
        { label: 'Venue', sortKey: 'venue' },
        { label: 'City', sortKey: 'city' },
        { label: 'Events', sortKey: 'count' },
        { label: 'Upcoming', sortKey: 'upcomingCount' },
        { label: 'Last event', sortKey: 'lastEventAt' },
      ]}
      searchFields={['venue', 'city', 'state']}
      searchPlaceholder="Venue, city or state"
      row={(facet) => (
        <tr key={`${facet.venue}-${facet.city}`} className="border-t border-portal-line">
          <td className="p-3">
            <Link
              to={placeLink({ venue: facet.venue })}
              className="text-portal-small font-bold text-brand hover:underline"
            >
              {facet.venue}
            </Link>
          </td>
          <td className="p-3 text-portal-small">
            {facet.city ? (
              <Link
                to={placeLink({ city: facet.city, usState: facet.state })}
                className="text-brand hover:underline"
              >
                {[facet.city, facet.state].filter(Boolean).join(', ')}
              </Link>
            ) : (
              <span className="text-portal-muted">—</span>
            )}
            {facet.venueAccountId ? (
              <Link
                to={`/accounts/${facet.venueAccountId}`}
                className="ml-2 text-portal-micro font-bold text-brand hover:underline"
              >
                account ↗
              </Link>
            ) : null}
          </td>
          <td className="p-3">
            <Pill>{facet.count}</Pill>
          </td>
          <td className="p-3">
            {facet.upcomingCount > 0 ? (
              <Pill tone="success">{facet.upcomingCount}</Pill>
            ) : (
              <span className="text-portal-small text-portal-muted">—</span>
            )}
          </td>
          <td className="p-3 whitespace-nowrap text-portal-small text-portal-muted">
            {facetDate(facet.lastEventAt)}
          </td>
        </tr>
      )}
    />
  );
}

/** The events for one place — any combination of city/state/venue filters. */
export function PlaceEventsPage(): React.ReactElement {
  const [params] = useSearchParams();
  const city = params.get('city') ?? undefined;
  const usState = params.get('usState') ?? undefined;
  const venue = params.get('venue') ?? undefined;
  const onDate = params.get('onDate') ?? undefined;

  const title = onDate
    ? new Date(`${onDate}T12:00:00Z`).toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : (venue ?? (city ? `${city}${usState ? `, ${usState}` : ''}` : (usState ?? 'Events')));
  const kind = onDate ? 'date' : venue ? 'venue' : city ? 'city' : 'state';

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow={`EVENTS BY ${kind.toUpperCase()}`}
        title={title}
        subtitle="Only events you're associated with, under the usual visibility rules."
      />
      <div className="mb-4 flex items-center gap-2 text-portal-small text-portal-muted">
        <BuildingIcon aria-hidden className="size-4" />
        <Link
          to={onDate ? '/events' : venue ? '/venues' : '/cities'}
          className="text-brand hover:underline"
        >
          {onDate ? 'All events' : venue ? 'All venues' : 'All cities'}
        </Link>
      </div>
      <EventsTable
        filter={{
          ...(city ? { city } : {}),
          ...(usState ? { usState } : {}),
          ...(venue ? { venue } : {}),
          ...(onDate ? { onDate } : {}),
        }}
      />
    </section>
  );
}
